import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import {extractNumber, convertShortDateInput} from './utils'

/*#__PURE__*/ dayjs.extend(customParseFormat)

const required = value => value && value.toString().trim() ? undefined : 'Please complete this field',

  // Similar to required but considers 0 and false as valid answers
  requiredAnyValue = value => {
    if (value === null || value === undefined ||
        typeof value === 'string' && value.trim() === '' ||
        typeof value === 'number' && (isNaN(value) || !isFinite(value))) {
      return 'Please complete this field'
    }
  },

  email = value =>
    value && !/^[A-Z0-9._+-]+@[A-Z0-9-]+(\.[A-Z]+)+$/i.test(value)
      ? 'Please enter a valid email'
      : undefined,

  dob = value => {
    const maxDate = dayjs()
        .subtract(13, 'years')
        .toDate(),
      minDate = dayjs()
        .subtract(120, 'years')
        .toDate(),
      format = 'MM/DD/YYYY',
      userDate = dayjs(value, format),
      year = value && value.split('/')[2]

    if (
      year && year.replace(/_/g, '').length != 4 || // Checks if the year has 4 digits
      !userDate.isValid() ||
      userDate.toDate() < minDate ||
      userDate.toDate() > maxDate
    ) {
      return `Please enter a date between ${dayjs(minDate).format(
        format
      )} and ${dayjs(maxDate).format(format)}.`
    }
  },

  dobGeneric = format => value => {
    const maxDate = dayjs()
        .subtract(13, 'years'),
      minDate = dayjs()
        .subtract(120, 'years'),
      userDate = dayjs(value, format, true)

    if (
      !userDate.isValid() ||
      userDate.toDate() < minDate.toDate() ||
      userDate.toDate() > maxDate.toDate()
    ) {
      return `Please enter a date between ${minDate.format(
        'MM/DD/YYYY'
      )} and ${maxDate.format('MM/DD/YYYY')}.`
    }
  },

  completionDate = value => {
    const [month, year] = value.split('/'),
      minDate = new Date(),
      maxDate = dayjs()
        .add(10, 'years')
        .toDate()

    minDate.setDate(1)

    if (!parseInt(month) && !parseInt(year)) {
      return
    }

    // eslint-disable-next-line
    const userDate = new Date(parseInt(year), parseInt(month) - 1, 2)

    if (isNaN(userDate.getTime()) || userDate < minDate || userDate > maxDate) {
      return `Please enter a date between ${dayjs(minDate).format(
        'MM/YYYY'
      )} and ${dayjs(maxDate).format('MM/YYYY')}.`
    }
  },

  validationPass = value =>
    value &&
    /\d+/g.test(value) &&
    /[A-Z]+/g.test(value) &&
    /[a-z]+/g.test(value) &&
    /\S{8,}/g.test(value) &&
    /^[\S]+/g.test(value) &&
    /.*[\S]+$/g.test(value)
      ? undefined
      : true,

  requiredOption = value =>
    value ? undefined : 'Please select an option from the list',

  SHORT_DATE_WITH_PREPENDED_DAY_FORMAT = 'DD/MM/YYYY',
  prependDayToShortDate = value => `01/${value}`,

  /**
 * Generic future date validation.
 * @param {string} format
 */
  futureDateGeneric = format => value => {
    const currentDate = dayjs(),
      userDate = dayjs(value, format, true)

    if (!userDate.isValid()) {
      return 'Please enter a valid date.'
    }

    if (currentDate.diff(userDate, 'days') > 0) {
      return 'Please enter a date in the future.'
    }
  },

  futureDate = value => {
    const currentDate = dayjs(),
      format = 'MM/DD/YYYY',
      userDate = dayjs(value, format, true)

    if (!userDate.isValid()) {
      return 'Please enter a valid date.'
    } else if (userDate.toDate() < currentDate.toDate()) {
      return `Please enter a date after ${currentDate.format('MM/DD/YYYY')}.`
    }
  },

  // Allow only dates in the future (format ISO)
  futureDateISO = futureDateGeneric('YYYY-MM-DD'),

  // Allow only dates in the future (format MM/YYYY)
  futureDateShort = value => {
    const currentDate = dayjs(),
      valueWithDay = prependDayToShortDate(value),
      userDate = dayjs(valueWithDay, SHORT_DATE_WITH_PREPENDED_DAY_FORMAT, true)

    if (!userDate.isValid()) {
      return 'Please enter a valid date.'
    }

    if (currentDate.diff(userDate, 'days') > 0) {
      return 'Please enter a date in the future.'
    }
  },

  // Generic past date validation.
  // Accepts {format}.
  // Usage:
  // Const pastDate = pastDateISO('MM/YYYY') // works like pastDateShort
  pastDateGeneric = format => value => {
    const currentDate = dayjs(),
      userDate = dayjs(value, format, true)

    if (!userDate.isValid()) {
      return 'Please enter a valid date.'
    }

    if (currentDate.diff(userDate, 'days') < 0) {
      return 'Please enter a date in the past.'
    }
  },

  // Allow only dates in the past.
  // Accepts a date format (ISO by default) and a value.
  pastDateISO = pastDateGeneric('YYYY-MM-DD'),

  // Allow only dates in the past.
  // Caution: doesn't use a strict parsing, use it carefully
  // @deprecated
  pastDate = value => {
    const currentDate = dayjs(),
      format = 'MM/DD/YYYY',
      userDate = dayjs(value, format)

    if (!userDate.isValid()) {
      return 'Please enter a valid date.'
    } else if (userDate.toDate() > currentDate.toDate()) {
      return `Please enter a date before ${currentDate.add(1, 'days').format('MM/DD/YYYY')}.`
    }
  },

  // Allow only dates in the past (format MM/YYYY)
  pastDateShort = value => {
    const currentDate = dayjs(),
      valueWithDay = prependDayToShortDate(value),

      userDate = dayjs(valueWithDay, SHORT_DATE_WITH_PREPENDED_DAY_FORMAT, true)

    if (!userDate.isValid()) {
      return 'Please enter a valid date.'
    }

    if (currentDate.diff(userDate, 'days') < 0) {
      return 'Please enter a date in the past.'
    }
  },

  phone = value => {
    const number = value.replace(/\D/g, '')

    if (number.length < 10) {
      return 'Please enter a valid phone number'
    } else if (number.charAt(0) === '1') {
      return 'Your phone number cannot start with a 1'
    }
  },

  tollPhone = value => {
    phone(value)
    const number = value.replace(/\D/g, ''),
      areaCode = number.substr(0, 3),
      tollFree = ['800', '811', '822', '833', '844', '855', '866', '877', '888']

    if (tollFree.includes(areaCode)) {
      return `Please enter a phone number that does not contain this area code: ${areaCode}`
    }
  },

  moreThanZero = value => !extractNumber(value) || extractNumber(value) < 0 ? 'Add a balance greater than $0' : undefined,

  validateSsn = ssn => {
    if (ssn && !ssn.match(/^\d{9}$/g)) {
      return 'Incorrect ssn'
    }

    return false
  },

  validateRepeatedSsn = ssn => {
    if (ssn && !ssn.match(/^\d{9}$/g)) {
      return 'Incorrect ssn'
    }

    if (ssn && ssn.match(/^(\d)\1+$/g)) {
      return 'Please enter a valid SSN'
    }

    return false
  },

  maxLength = max => value => value.length > max ? `Please enter at most ${max} characters.` : undefined,

  regexValidation = (regex, errorMsg) => value => {
    if (value && !regex.test(value)) {
      return errorMsg
    }
  },

  dateLimit = (unitsFromNow, unitType = 'years', format, printFormat) => value => {
    if (!['years', 'months', 'days'].includes(unitType)) {
      throw new Error('dateLimit: unitType must be "years", "months" or "days"')
    }

    const defaultFormat = value.length === 7 ? 'MM/YYYY' : 'MM/DD/YYYY',
      inputDateFormat = format !== 'MM/YYYY' ? format : 'MM/DD/YYYY',
      valueDate = dayjs(convertShortDateInput(value), inputDateFormat, true),
      printDateFormat = printFormat || defaultFormat,
      expectedDateLimit = dayjs().add(unitsFromNow, unitType).format(printDateFormat)

    if (valueDate.isValid()) {
      const diff = valueDate.diff(dayjs(), unitType, true)

      if (unitsFromNow < 0 && diff < unitsFromNow) {
        return `Please enter a date after ${dayjs().add(unitsFromNow, unitType).format(printDateFormat)}`
      }

      if (unitsFromNow > 0 && diff > unitsFromNow) {
        return `Please enter a date before ${dayjs().add(unitsFromNow, unitType).format(printDateFormat)}`
      }
    } else {
      return `Please enter a valid date ${unitsFromNow < 0 ? 'after' : 'before'} ${expectedDateLimit}`
    }
  },

  isPoBox = text => {
    if (typeof text !== 'string') {
      return
    }
    const pattern = /^p\.?o\.?\sbox\b|(post office box)/i

    return pattern.test(text.replace(/\s+/g, ' ').trim())
  },

  validatePoBox = value => {
    if (value && isPoBox(value)) {
      return 'Some Credible lenders will not accept a P.O. Box as an address. If possible, please add a valid street address.'
    }
  },

  validateStreetNumber = street => {
    if (!street || typeof street !== 'string') {
      return
    }
    const pattern = /^\d+\s/

    if (!pattern.test(street)) {
      return 'Please enter a street number if possible.'
    }
  },

  addressWarningTypes = {
    PO_BOX: 'PO_BOX',
    STREET_NUMBER: 'STREET_NUMBER'
  },

  // Returns a validation function and the address field to validate
  addressWarningValidations = {
    [addressWarningTypes.PO_BOX]: {
      addressSearch: 'name',
      street: 'street',
      func: validatePoBox
    },
    [addressWarningTypes.STREET_NUMBER]: {
      addressSearch: 'street',
      street: 'street',
      func: validateStreetNumber
    }
  },

  // Address field to validate for warning
  getAddressFieldToValidate = (warningType, componentName) => {
    const info = {...addressWarningValidations[warningType]},
      {[componentName]: addressField} = info

    return addressField
  },

  // Function to validate address field
  getWarningValidator = warningType => {
    const info = {...addressWarningValidations[warningType]},
      {func: validation} = info

    return validation
  },

  inputWarningTypes = {
    HOUSING: 'housing',
    INCOME: 'income'
  },

  /**
   * Returns a warning string if the input value violates a warning condition
   * @inputStr string input value
   * @warningType string value representing recognized warning type
   * @return undefined value if warning not necessary, warning string otherwise
   */
  moneyInputWarning = (inputStr, warningType) => {
    if (!inputStr || typeof inputStr !== 'string' || !warningType) {
      return
    }

    const payNum = Number(inputStr.replace(/[^0-9]+/g, ''))

    switch (warningType) {
      case inputWarningTypes.HOUSING:
        return payNum > 10000 ? `You spend ${inputStr}/month on housing. Is that right?` : undefined
      case inputWarningTypes.INCOME:
        return payNum > 1000000 ? `That's ${inputStr} a year. Is that right?` : undefined
    }
  }

export {
  required,
  email,
  dob,
  completionDate,
  validationPass,
  requiredOption,
  futureDate,
  futureDateISO,
  futureDateShort,
  pastDate,
  pastDateISO,
  pastDateShort,
  phone,
  tollPhone,
  moreThanZero,
  requiredAnyValue,
  validateSsn,
  validateRepeatedSsn,
  maxLength,
  regexValidation,
  dateLimit,
  dobGeneric,
  isPoBox,
  validatePoBox,
  validateStreetNumber,
  addressWarningTypes,
  addressWarningValidations,
  getAddressFieldToValidate,
  getWarningValidator,
  inputWarningTypes,
  moneyInputWarning
}
